import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import { getApi } from "../../api/api";
import { PromoInterface } from "../../common/types/interfaces";
import { useScrollPagination } from "../../common/hooks/useScrollPagination";
import { useCityId } from "../../common/hooks/useCityId";

const PAGE_LIMIT = 12;

export const usePromo = () => {
  const [searchParams] = useSearchParams();
  const search = searchParams.get("search") || "";

   const cityId = useCityId()

  const [items, setItems] = useState<PromoInterface[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    setOffset(0);
    setHasMore(true);
  }, [search, cityId]);

  useEffect(() => {
    if (!hasMore && offset > 0) return;
    if (isFetching || isFetchingMore) return;

    let cancelled = false;

    if (offset === 0) setIsFetching(true);
    else setIsFetchingMore(true);

    getApi()
      .getPromos({ search, limit: PAGE_LIMIT, offset, cityId })
      .then((res) => {
        if (!cancelled) {
          setItems((prev) =>
            offset === 0 ? res.data : [...prev, ...res.data],
          );
          setTotal(res.total);
          if (res.data.length < PAGE_LIMIT) setHasMore(false);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          if (offset === 0) setItems([]);
          setHasMore(false);
          setLoading(false);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsFetching(false);
          setIsFetchingMore(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, cityId, offset]);

  const loadMore = useCallback(() => {
    if (!isFetching && !isFetchingMore && hasMore) {
      setOffset((prev) => prev + PAGE_LIMIT);
    }
  }, [isFetching, isFetchingMore, hasMore]);

  const { lastItemRef } = useScrollPagination(
    hasMore,
    isFetchingMore,
    loadMore,
  );

  return {
    items,
    total,
    ready: !loading,
    isFetching,
    isFetchingMore,
    hasMore,
    loadMore,
    lastItemRef,
  };
};
